import { forwardRef, type ComponentProps } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import { cn } from "@/lib/utils";

interface NavLinkCompatProps extends Omit<ComponentProps<typeof Link>, "className"> {
  className?: string;
  activeClassName?: string;
  pendingClassName?: string;
  /** react-router NavLink `end`: only match the exact path, not descendants. */
  end?: boolean;
}

/**
 * react-router's <NavLink> matched the current pathname against `to`
 * (exact match, or prefix match on a `/` boundary). The compat shim's
 * NavLink is a plain Link, so active detection is re-implemented here
 * with useLocation. Pending state has no equivalent and is always false.
 */
const NavLink = forwardRef<HTMLAnchorElement, NavLinkCompatProps>(
  ({ className, activeClassName, pendingClassName: _pendingClassName, end, to, ...props }, ref) => {
    const location = useLocation();
    const target = typeof to === "string" ? to.split("?")[0]!.split("#")[0] : "";
    const current = location.pathname.replace(/\/+$/, "") || "/";
    const normalized = target!.replace(/\/+$/, "") || "/";
    const isActive =
      normalized === "/" || end
        ? current === normalized
        : current === normalized || current.startsWith(`${normalized}/`);

    return (
      <Link ref={ref} to={to} className={cn(className, isActive && activeClassName)} {...props} />
    );
  },
);

NavLink.displayName = "NavLink";

export { NavLink };
